import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { TYPE_VENDOR, TYPE_MANUFACTURER } from './plans';
import { ANDROID_PLATFORM, IOS_PLATFORM } from './platforms';

export const companyPlan = PropTypes.shape({
  type: PropTypes.oneOf([TYPE_MANUFACTURER, TYPE_VENDOR]),
  key: PropTypes.string,
  price: PropTypes.shape({
    amount: PropTypes.number,
    currency: PropTypes.string,
  }),
  max_qrs: PropTypes.number,
  max_catalog: PropTypes.number,
  max_manufacturers: PropTypes.number,
  max_collections: PropTypes.number,
  max_products: PropTypes.number,
});

export const manufacturerPreview = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  avatar: PropTypes.string,
});

export const radioGroupOption = PropTypes.shape({
  key: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
});

export const toast = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  message: PropTypes.string,
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']),
  duration: PropTypes.number,
});

export const account = PropTypes.shape({
  id: PropTypes.number.isRequired,
  avatar: PropTypes.string,
  email: PropTypes.string.isRequired,
  name: PropTypes.string,
  role: PropTypes.string.isRequired,
  availableMenus: PropTypes.arrayOf(PropTypes.string).isRequired,
  company: PropTypes.string,
});

export const locationProps = PropTypes.shape({
  path: PropTypes.string,
  key: PropTypes.string,
});

export const productPreview = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  photo: PropTypes.string,
  code: PropTypes.string,
  collection: PropTypes.string,
});

export const translatedField = PropTypes.shape({
  locale: PropTypes.string,
  value: PropTypes.string,
});

export const category = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
});

export const collection = PropTypes.shape({
  id: PropTypes.number,
  key: PropTypes.string,
});

export const detailedCollection = PropTypes.shape({
  name: PropTypes.string,
  products: PropTypes.arrayOf(productPreview),
});

export const simpleCollection = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
});

export const country = PropTypes.shape({
  name: PropTypes.string,
  code: PropTypes.string,
});

export const bundleInputValue = PropTypes.shape({
  name: PropTypes.string,
  bundle: PropTypes.object,
});

export const reduxFormInput = PropTypes.shape({
  name: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    bundleInputValue,
  ]),
  onChange: PropTypes.func,
});

export const reduxFormNumericInput = PropTypes.shape({
  value: PropTypes.any,
  onChange: PropTypes.func,
  name: PropTypes.string,
});

export const reduxFormLocalizedInput = PropTypes.shape({
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      locale: PropTypes.string,
      text: PropTypes.string,
    })),
    PropTypes.string,
  ]),
  onChange: PropTypes.func,
});

export const productOverrideInfo = PropTypes.shape({
  id: PropTypes.number,
  owner: PropTypes.string,
  created_at: PropTypes.string,
});

export const productDetails = PropTypes.shape({
  category,
  collection,
  country: PropTypes.oneOfType([country, PropTypes.string]),
  id: PropTypes.number,
  editable: PropTypes.bool,
  qr: PropTypes.string,
  bundles: PropTypes.shape({
    ios: PropTypes.string,
    android: PropTypes.string,
  }),
  names: PropTypes.arrayOf(translatedField),
  models: PropTypes.arrayOf(translatedField),
  descriptions: PropTypes.arrayOf(translatedField),
  requires_mount: PropTypes.bool,
  code: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  length: PropTypes.number,
  warranty_days: PropTypes.number,
  photos: PropTypes.arrayOf(PropTypes.string),
  product_overrides: PropTypes.arrayOf(productOverrideInfo),
});

export const idMatchParams = PropTypes.shape({
  params: PropTypes.shape({
    id: PropTypes.string,
  }),
});

export const manufacturerInfo = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  kind: PropTypes.string,
  phone: PropTypes.string,
  website: PropTypes.string,
  avatar: PropTypes.string,
  assigned: PropTypes.bool,
  product_count: PropTypes.number,
});

export const fullCollection = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  product: PropTypes.arrayOf(productPreview),
  loadingProducts: PropTypes.bool,
  page: PropTypes.number,
  loadingPage: PropTypes.number,
  totalPages: PropTypes.number,
});

export const applicationPlatform = PropTypes.oneOf([
  ANDROID_PLATFORM,
  IOS_PLATFORM,
]);

export const vendorSummary = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  product_count: PropTypes.number,
  created_at: PropTypes.string,
});

export const collaboratorInfo = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  avatar: PropTypes.string,
  email: PropTypes.string,
  pending: PropTypes.bool,
});

export const collaboratorPagination = PropTypes.shape({
  list: PropTypes.arrayOf(collaboratorInfo),
  page: PropTypes.number,
  totalPages: PropTypes.number,
  total: PropTypes.number,
  limit: PropTypes.number,
  loading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.any,
});

export const permission = PropTypes.shape({
  code: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired,
});

export const stringMapOf = shape => (
  (props, propName, componentName) => {
    const map = props[propName];
    Object.keys(map).forEach((key) => {
      PropTypes.checkPropTypes({ [key]: shape }, map, propName, componentName);
    });
  }
);

export const componentClass = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.func,
  PropTypes.oneOf([Fragment]),
]);

export const companyProfile = PropTypes.shape({
  name: PropTypes.string,
  kind: PropTypes.string,
  address: PropTypes.string,
  zip_code: PropTypes.string,
  cuit: PropTypes.string,
  phone: PropTypes.string,
  website: PropTypes.string,
  operational_countries: PropTypes.string,
});

export const accountProfile = PropTypes.shape({
  main_account: PropTypes.bool,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  avatar: PropTypes.string,
  email: PropTypes.string,
  company: companyProfile,
});



// WEBPACK FOOTER //
// ./src/utils/propTypes.js